import api from '@/lib/api';
import type {
  ApiResponse,
  PaginationMeta,
  LookupValue,
  BulkCreateResult,
  Building,
  CreateBuildingRequest,
  UpdateBuildingRequest,
  BuildingListQuery,
  BulkCreateBuildingsRequest,
  Zone,
  CreateZoneRequest,
  UpdateZoneRequest,
  ZoneListQuery,
  BulkCreateZonesRequest,
  UserPublic,
  CreateUserRequest,
  UpdateUserRequest,
  UserListQuery,
  BulkCreateUsersRequest,
  Student,
  CreateStudentRequest,
  UpdateStudentRequest,
  StudentListQuery,
  BulkCreateStudentsRequest,
  Class,
  CreateClassRequest,
  UpdateClassRequest,
  BulkCreateClassesRequest,
  Visitor,
  CreateVisitorRequest,
  UpdateVisitorRequest,
  VisitorListQuery,
  Tenant,
  UpdateTenantRequest,
  UpdateBrandingRequest,
  UpdateSettingsRequest,
  TenantUsageResponse,
  TenantAIUsageResponse,
  SetCustomDomainRequest,
  Gate,
  GateListQuery,
  CreateGateRequest,
  UpdateGateRequest,
  BulkCreateGatesRequest,
} from '@amuster/types';

// Building service
export const buildingService = {
  list(query?: BuildingListQuery) {
    return api.get<ApiResponse<Building[]> & { meta: PaginationMeta }>('/buildings', { params: query });
  },
  create(data: CreateBuildingRequest) {
    return api.post<ApiResponse<Building>>('/buildings', data);
  },
  getById(id: string) {
    return api.get<ApiResponse<Building>>(`/buildings/${id}`);
  },
  update(id: string, data: UpdateBuildingRequest) {
    return api.put<ApiResponse<Building>>(`/buildings/${id}`, data);
  },
  remove(id: string) {
    return api.delete<ApiResponse<null>>(`/buildings/${id}`);
  },
  bulkCreate(data: BulkCreateBuildingsRequest) {
    return api.post<ApiResponse<BulkCreateResult>>('/buildings/bulk', data);
  },
};

// Zone service
export const zoneService = {
  list(query?: ZoneListQuery) {
    return api.get<ApiResponse<Zone[]> & { meta: PaginationMeta }>('/zones', { params: query });
  },
  create(data: CreateZoneRequest) {
    return api.post<ApiResponse<Zone>>('/zones', data);
  },
  getById(id: string) {
    return api.get<ApiResponse<Zone>>(`/zones/${id}`);
  },
  update(id: string, data: UpdateZoneRequest) {
    return api.put<ApiResponse<Zone>>(`/zones/${id}`, data);
  },
  remove(id: string) {
    return api.delete<ApiResponse<null>>(`/zones/${id}`);
  },
  bulkCreate(data: BulkCreateZonesRequest) {
    return api.post<ApiResponse<BulkCreateResult>>('/zones/bulk', data);
  },
};

// Gate service
export const gateService = {
  list(query?: GateListQuery) {
    return api.get<ApiResponse<(Gate & { campus_name: string | null; building_name: string | null })[]> & { meta: PaginationMeta }>('/gates', { params: query });
  },
  create(data: CreateGateRequest) {
    return api.post<ApiResponse<Gate>>('/gates', data);
  },
  getById(id: string) {
    return api.get<ApiResponse<Gate & { campus_name: string | null; building_name: string | null }>>(`/gates/${id}`);
  },
  update(id: string, data: UpdateGateRequest) {
    return api.put<ApiResponse<Gate>>(`/gates/${id}`, data);
  },
  remove(id: string) {
    return api.delete<ApiResponse<null>>(`/gates/${id}`);
  },
  bulkCreate(data: BulkCreateGatesRequest) {
    return api.post<ApiResponse<BulkCreateResult>>('/gates/bulk', data);
  },
};

// User service
export const userService = {
  list(query?: UserListQuery) {
    return api.get<ApiResponse<UserPublic[]> & { meta: PaginationMeta }>('/users', { params: query });
  },
  create(data: CreateUserRequest) {
    return api.post<ApiResponse<UserPublic>>('/users', data);
  },
  getById(id: string) {
    return api.get<ApiResponse<UserPublic>>(`/users/${id}`);
  },
  update(id: string, data: UpdateUserRequest) {
    return api.put<ApiResponse<UserPublic>>(`/users/${id}`, data);
  },
  remove(id: string) {
    return api.delete<ApiResponse<null>>(`/users/${id}`);
  },
  bulkCreate(data: BulkCreateUsersRequest) {
    return api.post<ApiResponse<BulkCreateResult>>('/users/bulk', data);
  },
  resetPassword(id: string, newPassword: string) {
    return api.post<ApiResponse<null>>(`/users/${id}/reset-password`, { new_password: newPassword });
  },
  issueBadge(id: string) {
    return api.post<ApiResponse<{ badge_id: string; badge_code: string; issued_at: string }>>(
      `/users/${id}/badges/issue`
    );
  },
  reissueBadge(id: string, reason?: string) {
    return api.post<ApiResponse<{ badge_id: string; badge_code: string; issued_at: string }>>(
      `/users/${id}/badges/reissue`,
      { reason }
    );
  },
  revokeBadge(id: string, reason?: string) {
    return api.post<ApiResponse<{ message: string }>>(`/users/${id}/badges/revoke`, { reason });
  },
  listBadges(id: string) {
    return api.get<ApiResponse<Array<{
      id: string;
      issued_at: string;
      issued_by: string | null;
      revoked_at: string | null;
      revoked_by: string | null;
      revoke_reason: string | null;
      replaced_by_badge_id: string | null;
    }>>>(`/users/${id}/badges`);
  },
};

// Scanner Devices service (gate scanner provisioning)
export const scannerDevicesService = {
  list() {
    return api.get<ApiResponse<Array<{
      id: string;
      gate_id: string | null;
      name: string;
      status: string;
      token_version: number;
      issued_at: string;
      rotated_at: string | null;
      revoked_at: string | null;
      enrollment_expires_at: string | null;
      last_seen_at: string | null;
      last_used_at: string | null;
      last_used_ip: string | null;
      last_sync_at: string | null;
      cache_expires_at: string | null;
      created_at: string;
      updated_at: string;
    }>>>(`/scanner-devices`);
  },
  create(data: { name: string; gate_id?: string | null; enrollment_expires_in_seconds?: number }) {
    return api.post<ApiResponse<{ scanner_device: unknown; enrollment_code: string }>>(`/scanner-devices`, data);
  },
  regenerateEnrollment(id: string, data?: { enrollment_expires_in_seconds?: number }) {
    return api.post<ApiResponse<{ scanner_device: unknown; enrollment_code: string }>>(`/scanner-devices/${id}/regenerate-enrollment`, data ?? {});
  },
  rotateToken(id: string) {
    return api.post<ApiResponse<{ scanner_device: unknown; device_token: string }>>(`/scanner-devices/${id}/rotate-token`);
  },
  revoke(id: string, reason?: string) {
    return api.post<ApiResponse<{ message: string }>>(`/scanner-devices/${id}/revoke`, { reason });
  },
};

// Student service
export const studentService = {
  list(query?: StudentListQuery) {
    return api.get<ApiResponse<Student[]> & { meta: PaginationMeta }>('/students', { params: query });
  },
  create(data: CreateStudentRequest) {
    return api.post<ApiResponse<Student>>('/students', data);
  },
  getById(id: string) {
    return api.get<ApiResponse<Student>>(`/students/${id}`);
  },
  update(id: string, data: UpdateStudentRequest) {
    return api.put<ApiResponse<Student>>(`/students/${id}`, data);
  },
  remove(id: string) {
    return api.delete<ApiResponse<null>>(`/students/${id}`);
  },
  bulkCreate(data: BulkCreateStudentsRequest) {
    return api.post<ApiResponse<BulkCreateResult>>('/students/bulk', data);
  },
  getQr(id: string) {
    return api.get<ApiResponse<{ student_qr: string }>>(`/students/${id}/qr`);
  },
};

// Class service
export const classService = {
  list(query?: { page?: number; limit?: number; campus_id?: string; building_id?: string; grade_level?: string; is_active?: boolean; search?: string }) {
    return api.get<ApiResponse<Class[]> & { meta: PaginationMeta }>('/classes', { params: query });
  },
  create(data: CreateClassRequest) {
    return api.post<ApiResponse<Class>>('/classes', data);
  },
  getById(id: string) {
    return api.get<ApiResponse<Class>>(`/classes/${id}`);
  },
  update(id: string, data: UpdateClassRequest) {
    return api.put<ApiResponse<Class>>(`/classes/${id}`, data);
  },
  remove(id: string) {
    return api.delete<ApiResponse<null>>(`/classes/${id}`);
  },
  getStudents(id: string) {
    return api.get<ApiResponse<Student[]>>(`/classes/${id}/students`);
  },
  bulkCreate(data: BulkCreateClassesRequest) {
    return api.post<ApiResponse<BulkCreateResult>>('/classes/bulk', data);
  },
};

// Visitor service
export const visitorService = {
  list(query?: VisitorListQuery) {
    return api.get<ApiResponse<Visitor[]> & { meta: PaginationMeta }>('/visitors', { params: query });
  },
  create(data: CreateVisitorRequest) {
    return api.post<ApiResponse<Visitor>>('/visitors', data);
  },
  getById(id: string) {
    return api.get<ApiResponse<Visitor>>(`/visitors/${id}`);
  },
  update(id: string, data: UpdateVisitorRequest) {
    return api.put<ApiResponse<Visitor>>(`/visitors/${id}`, data);
  },
  remove(id: string) {
    return api.delete<ApiResponse<null>>(`/visitors/${id}`);
  },
  checkin(id: string) {
    return api.post<ApiResponse<Visitor>>(`/visitors/${id}/checkin`);
  },
  checkout(id: string) {
    return api.post<ApiResponse<Visitor>>(`/visitors/${id}/checkout`);
  },
};

// Tenant service
export const tenantService = {
  get() {
    return api.get<ApiResponse<Tenant>>('/tenant');
  },
  update(data: UpdateTenantRequest) {
    return api.put<ApiResponse<Tenant>>('/tenant', data);
  },
  updateBranding(data: UpdateBrandingRequest & { expected_updated_at?: string }) {
    return api.put<ApiResponse<Tenant>>('/tenant/branding', data, {
      headers: { 'X-Requested-With': 'XMLHttpRequest' },
    });
  },
  uploadBrandingLogo(file: File): Promise<{ data: { data: { url: string; url_png: string; url_small: string; original_url: string } } }> {
    const formData = new FormData();
    formData.append('file', file);
    return api.post('/tenant/branding/upload-logo', formData, {
      headers: {
        'Content-Type': 'multipart/form-data',
        'X-Requested-With': 'XMLHttpRequest',
      },
    });
  },
  updateSettings(data: UpdateSettingsRequest) {
    return api.put<ApiResponse<Tenant>>('/tenant/settings', data);
  },
  getUsage() {
    return api.get<ApiResponse<TenantUsageResponse>>('/tenant/usage');
  },
  getAIUsage() {
    return api.get<ApiResponse<TenantAIUsageResponse>>('/tenant/ai-usage');
  },
  setCustomDomain(data: SetCustomDomainRequest) {
    return api.post<ApiResponse<Tenant>>('/tenant/custom-domain', data);
  },
  removeCustomDomain() {
    return api.delete<ApiResponse<null>>('/tenant/custom-domain');
  },
};

// Lookup service (tenant-customizable lookup values)
export const lookupService = {
  listByCategory(category: string, query?: { include_inactive?: boolean }) {
    return api.get<ApiResponse<LookupValue[]>>(`/lookup/${category}`, { params: query });
  },
  create(category: string, data: { code: string; label_en: string; description?: string; sort_order?: number; is_active?: boolean }) {
    return api.post<ApiResponse<LookupValue>>(`/lookup/${category}`, data);
  },
  update(id: string, data: { label_en?: string; description?: string | null; sort_order?: number; is_active?: boolean }) {
    return api.put<ApiResponse<LookupValue>>(`/lookup/${id}`, data);
  },
  remove(id: string) {
    return api.delete<ApiResponse<LookupValue>>(`/lookup/${id}`);
  },
  bulkCreate(category: string, data: { values: { code: string; label_en: string; description?: string }[] }) {
    return api.post<ApiResponse<BulkCreateResult>>(
      `/lookup/${category}/bulk`,
      data,
    );
  },
};

// Platform branding service (platform admin only)
export const platformBrandingService = {
  get() {
    return api.get<ApiResponse<{ branding: { logo_url: string | null; app_name: string; theme_color: string }; updated_at: string }>>('/platform-admin/settings/branding');
  },
  update(data: { logo_url?: string | null; app_name?: string; theme_color?: string; expected_updated_at?: string }) {
    return api.put<ApiResponse<{ branding: { logo_url: string | null; app_name: string; theme_color: string }; updated_at: string }>>('/platform-admin/settings/branding', data, {
      headers: { 'X-Requested-With': 'XMLHttpRequest' },
    });
  },
  uploadLogo(file: File): Promise<{ data: { data: { url: string; url_png: string; url_small: string; original_url: string } } }> {
    const formData = new FormData();
    formData.append('file', file);
    return api.post('/platform-admin/branding/upload-logo', formData, {
      headers: {
        'Content-Type': 'multipart/form-data',
        'X-Requested-With': 'XMLHttpRequest',
      },
    });
  },
};
